import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  ActivityIndicator,
  useWindowDimensions,
  Platform,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { getApiUrl } from '../utils/apiConfig';
import { focusExercisesFromUser } from '../utils/sessionUser';
import {
  ReportWidget,
  layoutDashboardWidgets,
  renderReportWidgetBody,
  getReportingWidgetStyles,
  nextMode,
} from './ReportingWidgets';

const WIDGET_TWO_COLUMN_MIN_WIDTH = 768;

export default function FocusModeHomeWidgets({ user, theme }) {
  const { width: windowWidth } = useWindowDimensions();
  const isTwoColumn = windowWidth >= WIDGET_TWO_COLUMN_MIN_WIDTH;
  const styles = useMemo(() => getReportingWidgetStyles(theme), [theme]);

  const focusExercises = useMemo(() => focusExercisesFromUser(user), [user]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modes, setModes] = useState({});

  const loadStats = useCallback(async () => {
    if (!user?.id || focusExercises.length === 0) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/students/${encodeURIComponent(String(user.id))}/focus-exercise-stats`
      );
      if (!res.ok) throw new Error('Failed to load focus stats');
      const data = await res.json();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch {
      setItems(
        focusExercises.map((entry) => ({
          ...entry,
          training_count: 0,
        }))
      );
    } finally {
      setLoading(false);
    }
  }, [user?.id, focusExercises]);

  useFocusEffect(
    useCallback(() => {
      void loadStats();
    }, [loadStats])
  );

  useEffect(() => {
    setModes((prev) => {
      const next = { ...prev };
      focusExercises.forEach((entry, idx) => {
        const key = `focus-${entry.exercise_id}`;
        if (!next[key]) next[key] = idx % 2 === 0 ? 'number' : 'gauge';
      });
      return next;
    });
  }, [focusExercises]);

  if (!focusExercises.length) return null;

  const displayItems =
    items.length > 0
      ? items
      : focusExercises.map((entry) => ({ ...entry, training_count: 0 }));

  const widgetNodes = displayItems.map((item) => {
    const key = `focus-${item.exercise_id}`;
    const mode = modes[key] || 'number';
    const count = item.training_count ?? 0;
    const title = item.exercise_name || 'Exercise';
    const subtitle = [
      item.course_name,
      item.exercise_type_name ? `· ${item.exercise_type_name}` : '',
    ]
      .filter(Boolean)
      .join(' ');
    const series = [{ label: 'Sessions logged', value: count }];
    const gaugeMax = Math.max(10, count + 2);

    return (
      <ReportWidget
        key={key}
        title={title}
        mode={mode}
        onCycleMode={() =>
          setModes((prev) => ({
            ...prev,
            [key]: nextMode(prev[key] || 'number'),
          }))
        }
        theme={theme}
        styles={styles}
        containerStyle={isTwoColumn ? styles.widgetCardGrid : undefined}
      >
        {renderReportWidgetBody(
          mode,
          {
            series,
            numberValue: String(count),
            numberSubtitle: subtitle || 'Times trained (all time)',
            listLines: [
              `${title}: ${count} session log${count === 1 ? '' : 's'}`,
              subtitle,
            ],
            gaugeMax,
          },
          theme,
          styles
        )}
      </ReportWidget>
    );
  });

  return (
    <View
      style={styles.focusWidgetsSection}
      {...(Platform.OS === 'web' ? { dataSet: { focusModeHomeWidgets: 'true' } } : {})}
    >
      <Text style={[styles.focusWidgetsHeading, { color: theme.colors.heading }]}>
        Focus exercises
      </Text>
      {loading ? (
        <ActivityIndicator size="small" color={theme.colors.primary || '#d4af37'} />
      ) : null}
      {layoutDashboardWidgets(widgetNodes, isTwoColumn, styles)}
    </View>
  );
}
